export const ceilTo = (multiple: number) => (input: number) =>
  typeof multiple !== 'number' || multiple === 0
    ? 0
    : Math.round(Math.ceil(input / multiple) * multiple)

const EARTH_RADIUS_KM = 6371 // earth-radius in km
const RAD = Math.PI / 180

export const getDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
) => {
  const dLat = (lat2 - lat1) * RAD
  const dLon = (lon2 - lon1) * RAD

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * RAD) *
      Math.cos(lat2 * RAD) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return EARTH_RADIUS_KM * c
}

export const buildBoundingBox = ([lon, lat]: number[], distanceKm = 200) => {
  const diagonalDistance = distanceKm / Math.sqrt(2)
  const [neLat] = movePoint(lat, lon, diagonalDistance, 180)
  const [swLat] = movePoint(lat, lon, diagonalDistance, 0)
  const [, neLng] = movePoint(lat, lon, diagonalDistance, 270)
  const [, swLng] = movePoint(lat, lon, diagonalDistance, 90)

  return {
    neLat,
    swLat,
    neLng,
    swLng,
  }
}

/**
 * Calculates a new geographic point given a start point, distance, and bearing.
 * Uses the haversine-based spherical law of cosines formula.
 *
 * @author ChatGPT 4.0
 * @param lat Latitude of the starting point in degrees
 * @param lon Longitude of the starting point in degrees
 * @param distanceKm Distance to move from the point in kilometers
 * @param bearingDeg Bearing (direction) to move in degrees (0 = north, 90 = east, etc.)
 * @returns A new point [latitude, longitude] in degrees
 */
function movePoint(
  lat: number,
  lon: number,
  distanceKm: number,
  bearingDeg: number,
): [number, number] {
  // Convert linear distance to angular distance (in radians)
  const delta = distanceKm / EARTH_RADIUS_KM
  // Convert input bearing from degrees to radians
  const theta = (bearingDeg * Math.PI) / 180
  // Convert latitude and longitude from degrees to radians
  const phi1 = (lat * Math.PI) / 180
  const lambda1 = (lon * Math.PI) / 180

  // Calculate the new latitude using spherical trigonometry
  const sinPhi2 =
    Math.sin(phi1) * Math.cos(delta) +
    Math.cos(phi1) * Math.sin(delta) * Math.cos(theta)
  const phi2 = Math.asin(sinPhi2) // New latitude in radians

  // Calculate the new longitude using the spherical law of cosines
  const y = Math.sin(theta) * Math.sin(delta) * Math.cos(phi1)
  const x = Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2)
  const lambda2 = lambda1 + Math.atan2(y, x) // New longitude in radians

  // Convert results back from radians to degrees
  const newLat = (phi2 * 180) / Math.PI
  const newLon = (lambda2 * 180) / Math.PI

  return [newLat, newLon]
}

export const getCenterPointFromBoundingBox = (
  neLat: number,
  neLng: number,
  swLat: number,
  swLng: number,
) => {
  const lat = (neLat + swLat) / 2
  const lon = (neLng + swLng) / 2
  return [lon, lat]
}
